import { useState, useCallback, useMemo } from 'react';
import type { PresentationEvent } from '../../types/presentation.js';

export interface UseEventPlayerOptions {
  /** Called when all events have been processed */
  onComplete?: () => void;
}

export interface UseEventPlayerResult {
  /** Current PresentationEvent or null if no events */
  currentEvent: PresentationEvent | null;
  /** Current index in the events array */
  eventIndex: number;
  /** Whether all events have been processed */
  isComplete: boolean;
  /** Advance to the next event */
  advance: () => void;
  /** Reset to the first event */
  reset: () => void;
  /** Jump to a specific event index */
  goTo: (index: number) => void;
  /** Total number of events */
  totalEvents: number;
}

export function useEventPlayer(
  events: PresentationEvent[],
  options: UseEventPlayerOptions = {}
): UseEventPlayerResult {
  const { onComplete } = options;
  const [eventIndex, setEventIndex] = useState(0);

  const currentEvent = useMemo(() => {
    if (events.length === 0) return null;
    if (eventIndex >= events.length) return null;
    return events[eventIndex];
  }, [events, eventIndex]);

  const isComplete = eventIndex >= events.length;

  const advance = useCallback(() => {
    setEventIndex((prev) => {
      const next = prev + 1;
      if (next >= events.length) {
        onComplete?.();
      }
      return next;
    });
  }, [events.length, onComplete]);

  const reset = useCallback(() => {
    setEventIndex(0);
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (index >= 0 && index < events.length) {
        setEventIndex(index);
      }
    },
    [events.length]
  );

  return {
    currentEvent,
    eventIndex,
    isComplete,
    advance,
    reset,
    goTo,
    totalEvents: events.length,
  };
}
